import { setAlert } from "@/utils/redux/gigya/scene";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { setCookie as nextSetCookie } from "cookies-next";
import { accountLinkingMessage } from "@/helpers/sceneAccountLinkingMessage";
import { generateNonce } from "./noncehelper";
import { addDataLayer } from "./dataLayer";
import { encrypt } from "@/utils/encrypt";
import { setLogoutUser } from "@/utils/redux/gigya/logout/index";

export const checkSceneCard = async (res: { id_token: string }) => {
  const request = await fetch("/api/services/Inquire", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${encrypt(crypto.randomUUID()).toUpperCase()}`,
      focus: res.id_token,
      "x-nonce-token": generateNonce(),
    },
    body: JSON.stringify({}),
  });

  return request.json();
};

export const getLinkedInfo = async (res: { id_token: string }) => {
  const request = await fetch("/api/services/historyLite", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${encrypt(crypto.randomUUID()).toUpperCase()}`,
      focus: res.id_token,
      "x-nonce-token": generateNonce(),
    },
    body: JSON.stringify({}),
  });

  return request.json();
};

interface PopupData {
  lastShown: number;
  visits: number;
}
export const getCookie = (name: string): PopupData | null => {
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [key, value] = cookie.split("=");
    if (key === name) {
      try {
        return JSON.parse(decodeURIComponent(value)) as PopupData;
      } catch {
        return null;
      }
    }
  }
  return null;
};

export const setCookie = (
  name: string,
  value: object,
  days?: number,
  seconds?: number
): void => {
  const expires = new Date();
  expires.setTime(
    expires.getTime() +
      (days ?? 0) * 24 * 60 * 60 * 1000 +
      (seconds ?? 0) * 1000
  );
  document.cookie = `${name}=${encodeURIComponent(
    JSON.stringify(value)
  )}; expires=${expires.toUTCString()}; path=/`;
};

export const shouldShowPopup = (cookieName: string): boolean => {
  const cookieData = getCookie(cookieName);
  const now = Date.now();
  if (!cookieData) {
    return true;
  }
  const { lastShown, visits } = cookieData;
  if (visits > 3) {
    return false;
  }
  const daysSinceLastShown = (now - lastShown) / (1000 * 60 * 60 * 24);
  const intervals = [7, 21, 30];
  return daysSinceLastShown >= intervals[visits - 1];
};

export const handlePopupClose = ({
  cookie,
  suppressType,
}: {
  cookie: string;
  suppressType: string;
}) => {
  const cookieData: PopupData | null = getCookie(cookie);
  const visits = cookieData ? cookieData.visits + 1 : 1;
  setCookie(cookie, { lastShown: Date.now(), visits, suppressType }, 365);
};

export type VerifyCardsProps = {
  dispatch: any;
  gigya: any;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadLogin: (flow: string) => void;
  pathName: string;
  router: AppRouterInstance;
  callback?: () => Promise<any>;
  tokenResponse: {
    idToken: string;
    uid: string;
    cardNumber: string;
  };
};

async function setCookiesAndRedirect(
  router: any,
  pathName: string,
  dispatch: any,
  json: any,
  color: string,
  isError: boolean
) {
  await nextSetCookie("verify_code_req", "true", {
    maxAge: 15 * 60, // 15 minutes
  });
  await nextSetCookie("verify_resend_code", "true", {
    maxAge: 30, // 30 seconds
  });
  // Set the localStorage item for countdown
  localStorage.setItem("verify_code_countdown", "30");

  dispatch(
    setAlert({
      message: json?.message.endsWith(".")
        ? json?.message
        : json?.message + ".",
      color: color,
      isError: isError,
    })
  );
  // Now, perform the router push after cookies and localStorage are set
  router.push(`/verify-card?redirectUrl=${pathName}`);
}

export const triggerVerifyCard = async ({
  dispatch,
  gigya,
  setLoading,
  loadLogin,
  router,
  pathName,
  callback,
  tokenResponse,
}: VerifyCardsProps) => {
  setLoading(true);
  const request = await fetch("/api/services/linkAccount", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${encrypt(crypto.randomUUID()).toUpperCase()}`,
      focus: tokenResponse?.idToken,
      "x-nonce-token": generateNonce(),
    },
    body: JSON.stringify({}),
  });
  try {
    const json = await request.json();

    if (!json.status) {
      // Show Error Message
      dispatch(
        setAlert({
          message: json?.code
            ? accountLinkingMessage(json?.code, json?.message)
            : json?.message.endsWith(".")
              ? json?.message
              : json?.message + ".",
          color: "error",
          isError: true,
        })
      );
      return;
    } else {
      addDataLayer({
        event: "scene_registration",
        click_text: "verify_card",
        click_url: "step_2",
        page_title: document.title,
      });
      await setCookiesAndRedirect(
        router,
        pathName,
        dispatch,
        json,
        "success",
        false
      );
    }
  } catch {
    dispatch(
      setAlert({
        message: "Something went wrong. please try later.",
        color: "error",
        isError: true,
      })
    );
  } finally {
    await callback?.();
    setLoading(false);
  }
};

export const getTransactionHistory = async ({
  uid,
  id_token,
  enrollmentStatus,
}: {
  uid: string;
  id_token: string;
  enrollmentStatus: string;
}) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 180);
  const timeZone = process.env.NEXT_PUBLIC_TIME_ZONE || "America/Toronto";

  const endDate = new Date();

  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  try {
    const data = encrypt(
      JSON.stringify({
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        enrollmentStatus,
      })
    );
    const request = await fetch("/api/services/transactionHistory", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${encrypt(crypto.randomUUID()).toUpperCase()}`,
        focus: id_token,
        "x-nonce-token": generateNonce(),
      },
      body: JSON.stringify({ data }),
      next: {
        revalidate: 60 * 10,
      },
    });
    const json = await request.json();
    return json;
  } catch {
    return null;
  }
};
